import { useState, useEffect } from "react";

interface Review {
  id: string;
  restaurantId: string;
  author: string;
  rating: number;
  comment: string;
  date: string;
  helpful: number;
  helpedBy: string[];
  hidden?: boolean;
}

interface RestaurantReviewsProps {
  restaurantId: string;
  restaurantName: string;
}

const STORAGE_KEY = "restaurantReviews";

const StarRating = ({
  value,
  onChange,
  size = "md",
}: {
  value: number;
  onChange?: (v: number) => void;
  size?: "sm" | "md" | "lg";
}) => {
  const [hovered, setHovered] = useState(0);
  const sizeClass = size === "lg" ? "text-2xl" : size === "sm" ? "text-sm" : "text-lg";

  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => onChange?.(star)}
          onMouseEnter={() => onChange && setHovered(star)}
          onMouseLeave={() => onChange && setHovered(0)}
          className={`${sizeClass} transition-colors ${onChange ? "cursor-pointer" : "cursor-default"}`}
        >
          <i
            className={`${
              star <= (hovered || value) ? "ri-star-fill text-amber-400" : "ri-star-line text-stone-300"
            }`}
          ></i>
        </button>
      ))}
    </div>
  );
};

const RatingBar = ({ label, count, total }: { label: string; count: number; total: number }) => {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-stone-500 w-4 text-right">{label}</span>
      <div className="w-4 h-4 flex items-center justify-center">
        <i className="ri-star-fill text-amber-400 text-xs"></i>
      </div>
      <div className="flex-1 h-1.5 bg-stone-100 rounded-full overflow-hidden">
        <div
          className="h-full bg-amber-400 rounded-full transition-all duration-500"
          style={{ width: `${pct}%` }}
        ></div>
      </div>
      <span className="text-xs text-stone-400 w-6">{count}</span>
    </div>
  );
};

const RestaurantReviews = ({ restaurantId, restaurantName }: RestaurantReviewsProps) => {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [sortBy, setSortBy] = useState<"newest" | "highest" | "lowest" | "helpful">("newest");
  const [filterRating, setFilterRating] = useState(0);
  const [formData, setFormData] = useState({ author: "", rating: 0, comment: "" });
  const [formErrors, setFormErrors] = useState<{ author?: string; rating?: string; comment?: string }>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const all: Review[] = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
    // Only show visible (non-hidden) reviews for this restaurant
    setReviews(all.filter((r) => r.restaurantId === restaurantId && !r.hidden));
  }, [restaurantId]);

  const saveReviews = (updated: Review[]) => {
    const all: Review[] = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
    // Keep all reviews (including hidden ones from other restaurants) intact
    const others = all.filter((r) => r.restaurantId !== restaurantId);
    // Merge: keep hidden reviews in storage but don't show them in state
    const hiddenFromThisRestaurant = all.filter((r) => r.restaurantId === restaurantId && r.hidden);
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...others, ...hiddenFromThisRestaurant, ...updated]));
    setReviews(updated);
  };

  const avgRating = reviews.length > 0
    ? reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length
    : 0;

  const ratingCounts = [5, 4, 3, 2, 1].map((star) => ({
    star,
    count: reviews.filter((r) => r.rating === star).length,
  }));

  const validate = () => {
    const e: typeof formErrors = {};
    if (!formData.author.trim()) e.author = "İsim zorunludur";
    if (!formData.rating) e.rating = "Puan seçiniz";
    if (!formData.comment.trim()) e.comment = "Yorum zorunludur";
    else if (formData.comment.length < 10) e.comment = "En az 10 karakter giriniz";
    setFormErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);

    setTimeout(() => {
      const newReview: Review = {
        id: `rev-${Date.now()}`,
        restaurantId,
        author: formData.author.trim(),
        rating: formData.rating,
        comment: formData.comment.trim(),
        date: new Date().toISOString(),
        helpful: 0,
        helpedBy: [],
      };
      const updated = [newReview, ...reviews];
      saveReviews(updated);
      setFormData({ author: "", rating: 0, comment: "" });
      setFormErrors({});
      setSubmitting(false);
      setSubmitted(true);
      setShowForm(false);
      setTimeout(() => setSubmitted(false), 4000);
    }, 600);
  };

  const handleHelpful = (reviewId: string) => {
    const sessionKey = `helpful_${reviewId}`;
    if (sessionStorage.getItem(sessionKey)) return;
    sessionStorage.setItem(sessionKey, "1");
    const updated = reviews.map((r) =>
      r.id === reviewId ? { ...r, helpful: r.helpful + 1 } : r
    );
    saveReviews(updated);
  };

  const sortedFiltered = [...reviews]
    .filter((r) => filterRating === 0 || r.rating === filterRating)
    .sort((a, b) => {
      if (sortBy === "newest") return new Date(b.date).getTime() - new Date(a.date).getTime();
      if (sortBy === "highest") return b.rating - a.rating;
      if (sortBy === "lowest") return a.rating - b.rating;
      if (sortBy === "helpful") return b.helpful - a.helpful;
      return 0;
    });

  const formatDate = (iso: string) => {
    return new Date(iso).toLocaleDateString("tr-TR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  return (
    <section className="mt-14">
      {/* Section Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h2
          className="text-xl font-bold text-stone-900"
          style={{ fontFamily: "'Playfair Display', serif" }}
        >
          Ziyaretçi Yorumları
        </h2>
        <button
          onClick={() => setShowForm((v) => !v)}
          className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-full text-sm font-medium hover:bg-amber-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className={showForm ? "ri-close-line" : "ri-edit-line"}></i>
          {showForm ? "İptal" : "Yorum Yaz"}
        </button>
      </div>

      {/* Success Toast */}
      {submitted && (
        <div className="mb-4 flex items-center gap-3 p-3 bg-emerald-50 border border-emerald-200 rounded-xl">
          <div className="w-6 h-6 flex items-center justify-center">
            <i className="ri-checkbox-circle-fill text-emerald-500"></i>
          </div>
          <p className="text-sm text-emerald-700 font-medium">Yorumunuz başarıyla eklendi!</p>
        </div>
      )}

      {/* Review Form */}
      {showForm && (
        <div className="mb-8 bg-amber-50 rounded-2xl border border-amber-100 p-5 md:p-6">
          <h3 className="text-base font-bold text-stone-900 mb-4">
            <span className="text-amber-600">{restaurantName}</span> için yorum yaz
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-stone-600 mb-1.5">İsminiz *</label>
                <input
                  type="text"
                  value={formData.author}
                  onChange={(e) => setFormData((p) => ({ ...p, author: e.target.value }))}
                  placeholder="Adınız veya takma adınız"
                  className={`w-full border rounded-lg px-3 py-2.5 text-sm text-stone-800 outline-none transition-colors bg-white
                    ${formErrors.author ? "border-rose-400" : "border-stone-200 focus:border-amber-400"}`}
                />
                {formErrors.author && <p className="text-rose-500 text-xs mt-1">{formErrors.author}</p>}
              </div>
              <div>
                <label className="block text-xs font-semibold text-stone-600 mb-1.5">Puanınız *</label>
                <div className="flex items-center gap-3 h-10">
                  <StarRating
                    value={formData.rating}
                    onChange={(v) => setFormData((p) => ({ ...p, rating: v }))}
                    size="lg"
                  />
                  {formData.rating > 0 && (
                    <span className="text-sm font-semibold text-amber-600">
                      {["", "Çok Kötü", "Kötü", "Orta", "İyi", "Mükemmel"][formData.rating]}
                    </span>
                  )}
                </div>
                {formErrors.rating && <p className="text-rose-500 text-xs mt-1">{formErrors.rating}</p>}
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-stone-600 mb-1.5">
                Yorumunuz * <span className="text-stone-400 font-normal">({formData.comment.length}/500)</span>
              </label>
              <textarea
                value={formData.comment}
                onChange={(e) => {
                  if (e.target.value.length <= 500)
                    setFormData((p) => ({ ...p, comment: e.target.value }));
                }}
                rows={4}
                placeholder={`${restaurantName} hakkındaki deneyiminizi paylaşın...`}
                className={`w-full border rounded-lg px-3 py-2.5 text-sm text-stone-800 outline-none transition-colors resize-none bg-white
                  ${formErrors.comment ? "border-rose-400" : "border-stone-200 focus:border-amber-400"}`}
              />
              {formErrors.comment && <p className="text-rose-500 text-xs mt-1">{formErrors.comment}</p>}
            </div>
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={submitting}
                className="flex items-center gap-2 px-5 py-2.5 bg-amber-500 text-white rounded-full text-sm font-medium hover:bg-amber-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
              >
                {submitting ? (
                  <><i className="ri-loader-4-line animate-spin"></i> Gönderiliyor...</>
                ) : (
                  <><i className="ri-send-plane-line"></i> Yorumu Gönder</>
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Rating Summary */}
      {reviews.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8 p-5 bg-stone-50 rounded-2xl">
          {/* Average */}
          <div className="flex flex-col items-center justify-center text-center">
            <span className="text-5xl font-bold text-stone-900" style={{ fontFamily: "'Playfair Display', serif" }}>
              {avgRating.toFixed(1)}
            </span>
            <StarRating value={Math.round(avgRating)} size="md" />
            <p className="text-xs text-stone-400 mt-1">{reviews.length} yorum</p>
          </div>

          {/* Bar Chart */}
          <div className="md:col-span-2 space-y-1.5 justify-center flex flex-col">
            {ratingCounts.map(({ star, count }) => (
              <RatingBar key={star} label={String(star)} count={count} total={reviews.length} />
            ))}
          </div>
        </div>
      )}

      {/* Filters & Sort */}
      {reviews.length > 0 && (
        <div className="flex items-center gap-3 mb-5 flex-wrap">
          <div className="flex items-center gap-1.5 flex-wrap">
            <button
              onClick={() => setFilterRating(0)}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors cursor-pointer whitespace-nowrap
                ${filterRating === 0 ? "bg-stone-900 text-white border-stone-900" : "border-stone-200 text-stone-500 hover:border-stone-300"}`}
            >
              Tümü ({reviews.length})
            </button>
            {[5, 4, 3, 2, 1].map((star) => {
              const cnt = reviews.filter((r) => r.rating === star).length;
              if (cnt === 0) return null;
              return (
                <button
                  key={star}
                  onClick={() => setFilterRating(star)}
                  className={`flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border transition-colors cursor-pointer whitespace-nowrap
                    ${filterRating === star ? "bg-amber-500 text-white border-amber-500" : "border-stone-200 text-stone-500 hover:border-stone-300"}`}
                >
                  <i className="ri-star-fill text-xs"></i> {star} ({cnt})
                </button>
              );
            })}
          </div>
          <div className="ml-auto">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
              className="text-xs border border-stone-200 rounded-lg px-3 py-1.5 text-stone-600 outline-none cursor-pointer focus:border-amber-400 transition-colors"
            >
              <option value="newest">En Yeni</option>
              <option value="highest">En Yüksek Puan</option>
              <option value="lowest">En Düşük Puan</option>
              <option value="helpful">En Faydalı</option>
            </select>
          </div>
        </div>
      )}

      {/* Reviews List */}
      {sortedFiltered.length === 0 ? (
        <div className="text-center py-12 bg-stone-50 rounded-2xl">
          <div className="w-14 h-14 flex items-center justify-center mx-auto mb-3 bg-stone-100 rounded-full">
            <i className="ri-chat-3-line text-stone-400 text-2xl"></i>
          </div>
          <p className="text-stone-500 font-medium text-sm">
            {reviews.length === 0 ? "Henüz yorum yapılmamış" : "Bu filtreye uygun yorum yok"}
          </p>
          <p className="text-stone-400 text-xs mt-1">
            {reviews.length === 0
              ? "İlk yorumu siz yapın!"
              : "Farklı bir filtre deneyin"}
          </p>
          {reviews.length === 0 && (
            <button
              onClick={() => setShowForm(true)}
              className="mt-4 px-4 py-2 bg-amber-500 text-white rounded-full text-xs font-medium hover:bg-amber-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              Yorum Yaz
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {sortedFiltered.map((review) => (
            <div key={review.id} className="bg-white border border-stone-100 rounded-2xl p-5 hover:border-stone-200 transition-colors">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center bg-amber-100 rounded-full flex-shrink-0">
                    <span className="text-amber-700 font-bold text-sm">
                      {review.author.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-stone-900">{review.author}</p>
                    <p className="text-xs text-stone-400">{formatDate(review.date)}</p>
                  </div>
                </div>
                <StarRating value={review.rating} size="sm" />
              </div>
              <p className="text-sm text-stone-600 leading-relaxed">{review.comment}</p>
              <div className="flex items-center gap-2 mt-3 pt-3 border-t border-stone-50">
                <button
                  onClick={() => handleHelpful(review.id)}
                  className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border transition-colors cursor-pointer whitespace-nowrap
                    ${sessionStorage.getItem(`helpful_${review.id}`)
                      ? "bg-stone-100 text-stone-500 border-stone-200"
                      : "border-stone-200 text-stone-400 hover:border-stone-300 hover:text-stone-600"}`}
                >
                  <i className="ri-thumb-up-line"></i>
                  Faydalı {review.helpful > 0 && `(${review.helpful})`}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default RestaurantReviews;
